import { useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, FileText, Loader2, Music, Play } from "lucide-react";
import type { LibraryItem } from "@/api/models/api-types";
import { cn } from "@/lib/utils";
import { useCanHover } from "../hooks/use-can-hover";
import { useNearViewport } from "../hooks/use-near-viewport";
import { getFileExtension, getItemUrl, getMediaKind } from "../lib/library-media";

interface AssetTileProps {
  item: LibraryItem;
  isSelected?: boolean;
  /** Seçilemez durumda medya soluklaşır; üst katmanlar yine çizilir */
  isDisabled?: boolean;
  className?: string;
  /** Medyanın üstündeki etkileşim katmanları (link, butonlar, rozetler) */
  children?: ReactNode;
}

/**
 * Library kartının görsel çekirdeği: medya, tembel video yükleme, hover'da sessiz
 * oynatma ve seçim halkası. Hiçbir store'a bağlı değildir; Library ve dosya seçici
 * kendi etkileşimlerini `children` ile üstüne koyar.
 */
export const AssetTile = ({ item, isSelected = false, isDisabled = false, className, children }: AssetTileProps) => {
  const { t } = useTranslation();
  const canHover = useCanHover();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const tileRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const kind = getMediaKind(item);
  const url = getItemUrl(item);
  // Video kaynağı kart ekrana yaklaşınca verilir; ekran dışındaki videolar hiç indirilmez
  const isNearViewport = useNearViewport(tileRef, { enabled: kind === "video" });
  const isVideoBuffering = kind === "video" && isHovered && !isPlaying;

  const handleMouseEnter = () => {
    if (kind !== "video" || !canHover) return;
    setIsHovered(true);
    videoRef.current?.play().catch(() => undefined);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  };

  return (
    <div
      ref={tileRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "group relative aspect-square overflow-hidden rounded-2xl bg-bg-secondary dark:bg-gray-800 transition-shadow duration-200",
        isSelected
          ? "ring-2 ring-gray-900 dark:ring-white ring-offset-2 ring-offset-bg-primary dark:ring-offset-gray-900"
          : "ring-1 ring-black/5 dark:ring-white/5",
        className
      )}
    >
      <div className={cn("absolute inset-0 transition-opacity duration-200", isDisabled && "opacity-35 grayscale")}>
        {kind === "image" && (
          <img
            src={url}
            alt={item.title}
            loading="lazy"
            decoding="async"
            onLoad={() => setIsLoaded(true)}
            onError={() => setIsLoaded(true)}
            className={cn(
              "absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-500 group-hover:scale-[1.03]",
              isLoaded ? "opacity-100" : "opacity-0"
            )}
          />
        )}

        {kind === "video" && isNearViewport && (
          <video
            ref={videoRef}
            src={`${url}#t=0.1`}
            muted
            loop
            playsInline
            preload="metadata"
            onLoadedData={() => setIsLoaded(true)}
            onError={() => setIsLoaded(true)}
            onPlaying={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onWaiting={() => setIsPlaying(false)}
            className={cn(
              "absolute inset-0 w-full h-full object-cover transition-opacity duration-500",
              isLoaded ? "opacity-100" : "opacity-0"
            )}
          />
        )}

        {kind === "audio" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-5 text-center bg-gradient-to-br from-violet-100 to-sky-100 dark:from-violet-950 dark:to-sky-950">
            <div className="w-14 h-14 rounded-full bg-white/70 dark:bg-white/10 flex items-center justify-center">
              <Music className="w-6 h-6 text-violet-600 dark:text-violet-300" />
            </div>
            <div className="flex items-end gap-[3px] h-6" aria-hidden>
              {[40, 70, 100, 60, 85, 45, 75, 55, 90, 35].map((height, index) => (
                <span
                  key={index}
                  style={{ height: `${height}%` }}
                  className="w-[3px] rounded-full bg-violet-500/60 dark:bg-violet-300/60"
                />
              ))}
            </div>
            <p className="text-xs text-text-secondary dark:text-gray-300 line-clamp-2">
              {item.prompt || item.title || t("library.audio")}
            </p>
          </div>
        )}

        {kind === "document" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-950 dark:to-orange-950">
            <div className="w-14 h-14 rounded-full bg-white/70 dark:bg-white/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-rose-600 dark:text-rose-300" />
            </div>
            <span className="text-xs font-semibold text-text-secondary dark:text-gray-300">
              {getFileExtension(item).toUpperCase() || "DOC"}
            </span>
          </div>
        )}

        {!isLoaded && (kind === "image" || kind === "video") && (
          <div className="absolute inset-0 animate-pulse bg-bg-tertiary dark:bg-gray-700" />
        )}

        {/* Video göstergesi — hover'da oynamaya başlayana kadar spinner */}
        {kind === "video" && (
          <div
            className={cn(
              "pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-200",
              isVideoBuffering ? "opacity-100" : "group-hover:opacity-0"
            )}
          >
            <div className="w-10 h-10 rounded-full bg-black/35 backdrop-blur-md flex items-center justify-center">
              {isVideoBuffering ? (
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              ) : (
                <Play className="w-4 h-4 text-white fill-white ml-0.5" />
              )}
            </div>
          </div>
        )}
      </div>

      {children}
    </div>
  );
};

interface AssetTileCheckboxProps {
  isSelected: boolean;
  /** Seçim modunda ya da dokunmatikte hover beklemeden görünür */
  isAlwaysVisible?: boolean;
  onToggle: () => void;
  label: string;
  disabled?: boolean;
}

/** Kartın sol üstündeki seçim kutusu. */
export const AssetTileCheckbox = ({ isSelected, isAlwaysVisible, onToggle, label, disabled }: AssetTileCheckboxProps) => {
  const canHover = useCanHover();
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={isSelected}
      aria-label={label}
      className={cn(
        "absolute top-2.5 left-2.5 z-20 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-200 disabled:cursor-not-allowed",
        isSelected
          ? "bg-white border-white text-gray-900 opacity-100"
          : "border-white/80 bg-black/20 text-transparent backdrop-blur-sm hover:bg-black/40",
        !isSelected && !isAlwaysVisible && (canHover ? "opacity-0 group-hover:opacity-100" : "opacity-100")
      )}
    >
      <Check className="w-3.5 h-3.5" strokeWidth={3} />
    </button>
  );
};
